import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	IDroppedFile,
	IFileTypeConfig,
} from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { Subject } from 'rxjs';
import { FileSelectionsService } from '@shared/ui-components/au-file-loader/services/file-selections.service';
import { takeUntil } from 'rxjs/operators';
import { FileUtilities } from '@shared/ui-components/au-file-loader/helpers/file-utilities';

import { MatCheckbox } from '@angular/material/checkbox';
import { AuFileNameComponent } from '../../au-file-name/au-file-name.component';
import { AuFileErrorViewerComponent } from './file-error-viewer/au-file-error-viewer.component';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { AuLoadProgressBarComponent } from './load-progress-bar/au-load-progress-bar.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'au-files-list-item',
	templateUrl: './au-files-list-item.component.html',
	styleUrls: ['./au-files-list-item.component.scss'],
	imports: [
		MatCheckbox,
		AuFileNameComponent,
		AuFileErrorViewerComponent,
		MatIconButton,
		MatTooltip,
		MatIcon,
		AuLoadProgressBarComponent,
		MatProgressSpinner,
		NgxTolgeeModule,
	],
})
export class AuFilesListItemComponent implements OnInit, OnInit, OnChanges, OnDestroy {
	@Input() fileTypesConfig!: IFileTypeConfig;
	@Input() fileData: Partial<IDroppedFile>;
	@Input() disableBulkSelections: boolean;
	@Input() showDeleteButton: boolean;
	@Input() showCancelButton: boolean;

	@Output() deletePressed = new EventEmitter<void>();
	@Output() cancelPressed = new EventEmitter<void>();
	@Output() retryToLoadFile = new EventEmitter<number>();
	@Output() openFile = new EventEmitter<number>();

	isFileSelected: boolean;
	icon: string;
	deletionLoaderVisible: boolean;
	fileUtilities: FileUtilities;

	private destroy$: Subject<void> = new Subject<void>();

	constructor(private selectionService: FileSelectionsService) {}

	ngOnInit() {
		this.selectionService.selections$
			.pipe(takeUntil(this.destroy$))
			.subscribe((selections: string[]) => {
				this.isFileSelected = selections.includes(this.fileData.pk);
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.fileTypesConfig?.currentValue) {
			this.fileUtilities = new FileUtilities(changes.fileTypesConfig?.currentValue);
		}

		if (changes.fileData) {
			this.icon = this.getIcon();
		}

		if (changes.fileData?.currentValue.deletionLoader) {
			this.fileData.deletionLoader
				.pipe(takeUntil(this.destroy$))
				.subscribe((value: boolean) => {
					this.deletionLoaderVisible = value;
				});
		}
	}

	private getIcon(): string {
		const type = this.fileUtilities.getFileType(
			this.fileData.name,
			this.fileData.contentType
		);

		if (type === 'file-unknown') {
			return type;
		}

		return this.fileTypesConfig[type]?.icon;
	}

	brakeLoading(): void {
		this.fileData.requestBrakeSubject.next();
		this.fileData.requestBrakeSubject.complete();
	}

	retryLoading() {
		this.fileData.requestBrakeSubject.next();
		this.fileData.requestBrakeSubject.complete();
		this.fileData.errors = {};
		this.retryToLoadFile.emit(this.fileData.index);
	}

	open() {
		if (this.fileData.loadingProgress === 100 && !this.hasError()) {
			this.openFile.emit(this.fileData.index);
		}
	}

	selectionChange(checked) {
		if (checked) {
			this.isFileSelected = true;
			this.selectionService.addToSelectionsList(this.fileData.pk);
		} else {
			this.isFileSelected = false;
			this.selectionService.removeFromSelectionsList(this.fileData.pk);
		}
	}

	hasError(): boolean {
		if (this.fileData?.errors) {
			return Object.keys(this.fileData?.errors)?.length > 0;
		}

		return false;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	delete() {
		this.deletionLoaderVisible = true;
		this.deletePressed.emit();
	}

	cancel() {
		this.brakeLoading();
		this.cancelPressed.emit();
	}
}
